import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChatCircle, Clock, PaperPlaneTilt, Lock, Tag, Buildings, FolderOpen, User, Star } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useStatuses } from "../context/StatusContext";
import { StatusBadge, PriorityBadge, ClientRepliedBadge } from "../components/Badges";
import { ALL_STATUSES, STATUS_LABELS, PRIORITIES, formatDate, initials, ROLE_LABELS, timeAgo, slaState, humanizeDuration, subtypesText } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { SearchableSelect } from "../components/ui/searchable-select";
import useFieldConfigs from "../hooks/useFieldConfigs";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { AttachmentList, AttachmentUploader } from "../components/Attachments";
import DOMPurify from "dompurify";
import { LinkSimple } from "@phosphor-icons/react";

const DAY_ABBR = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function formatWorkDays(days = []) {
  const d = [...days].filter((v) => v >= 0 && v <= 6).sort((a, b) => a - b);
  if (!d.length) return "";
  const contiguous = d.every((v, i) => i === 0 || v === d[i - 1] + 1);
  if (contiguous && d.length > 2) return `${DAY_ABBR[d[0]]}–${DAY_ABBR[d[d.length - 1]]}`;
  return d.map((v) => DAY_ABBR[v]).join(", ");
}

function StarRating({ value = 0, onChange, size = 20 }) {
  return (
    <div className="flex items-center gap-1" data-testid="rating-input">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onChange}
          onClick={() => onChange && onChange(n)}
          className={onChange ? "cursor-pointer" : "cursor-default"}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star size={size} weight={n <= value ? "fill" : "regular"} className={n <= value ? "text-amber-500" : "text-slate-300"} />
        </button>
      ))}
    </div>
  );
}

const ACTIVITY_ACTION_LABELS = {
  sla_breached: "SLA breached",
  sla_approaching: "SLA approaching",
};
const humanizeActivityAction = (a) => ACTIVITY_ACTION_LABELS[a] || (a || "").replace(/_/g, " ");

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { list: statusList } = useStatuses();

  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [worklogs, setWorklogs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [journey, setJourney] = useState(null);
  const [team, setTeam] = useState([]);
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [priorityOpts, setPriorityOpts] = useState(PRIORITIES);
  const [error, setError] = useState("");

  // Remark modal state
  const [pendingStatus, setPendingStatus] = useState(null);
  const [remark, setRemark] = useState("");
  const [remarkErr, setRemarkErr] = useState("");
  const [remarkSubmitting, setRemarkSubmitting] = useState(false);

  const [commentBody, setCommentBody] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);
  const [commentAssign, setCommentAssign] = useState("");
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [worklog, setWorklog] = useState({ date: new Date().toISOString().slice(0, 10), hours: 1, description: "" });

  // Role-filtered status options from tenant statuses
  const isClient = ["client_admin", "client_user"].includes(user.role);
  const statusOptions = statusList.filter((s) => s.enabled
    && (!isClient || s.client_settable)
    && (isClient || !s.hide_from_staff));

  const canManage = user.role === "super_admin" || user.role === "project_manager";
  const canDev = user.role === "team_member" || canManage;
  const canEditPriority = canManage
    || user.role === "client_admin"
    || (user.role === "client_user" && ticket?.created_by === user.id);
  const { fieldOn } = useFieldConfigs();
  const fmtDur = (secs) => (secs ? humanizeDuration(secs * 1000) : "—");

  const refresh = async () => {
    try {
      const [t, c, w, a] = await Promise.all([
        api.get(`/tickets/${id}`),
        api.get(`/tickets/${id}/comments`),
        api.get(`/tickets/${id}/worklogs`),
        api.get(`/tickets/${id}/activities`),
      ]);
      setTicket(t.data);
      setComments(c.data);
      setWorklogs(w.data);
      setActivities(a.data);
      if (!isClient) {
        api.get(`/tickets/${id}/journey`).then((r) => setJourney(r.data)).catch(() => {});
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { refresh(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (canDev) api.get("/users").then((r) => setTeam(r.data.filter((u) => ["super_admin", "team_member", "project_manager"].includes(u.role))));
  }, [canDev]);
  useEffect(() => {
    api.get("/custom-fields?active_only=true").then((r) => setCustomFieldDefs(r.data)).catch(() => {});
    api.get("/masters/priority?active_only=true").then((r) => setPriorityOpts(r.data.map((p) => p.value))).catch(() => {});
  }, []);

  const totalLogged = useMemo(() => worklogs.reduce((s, w) => s + (w.hours || 0), 0), [worklogs]);

  const openStatusModal = (v) => {
    if (v === ticket.status) return;
    setPendingStatus(v);
    setRemark("");
    setRemarkErr("");
  };

  const cancelStatusModal = () => {
    setPendingStatus(null);
    setRemark("");
    setRemarkErr("");
  };

  const confirmStatusChange = async () => {
    if (!remark.trim()) { setRemarkErr("A remark is required to change status."); return; }
    setRemarkSubmitting(true);
    try {
      const { data } = await api.patch(`/tickets/${id}`, { status: pendingStatus, remark: remark.trim() });
      setTicket((t) => ({ ...t, ...data }));
      setPendingStatus(null);
      setRemark("");
      setRemarkErr("");
      refresh();
    } catch (e) { setRemarkErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setRemarkSubmitting(false); }
  };
  const onPriorityChange = async (v) => {
    try {
      const { data } = await api.patch(`/tickets/${id}`, { priority: v });
      setTicket((t) => ({ ...t, ...data }));
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };
  const onAssign = async (v) => {
    try {
      const { data } = await api.patch(`/tickets/${id}`, { assigned_to: v });
      setTicket((t) => ({ ...t, ...data }));
      refresh();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!commentBody.trim() && commentFiles.length === 0) return;
    try {
      const body = { body: commentBody, is_internal: isInternal, attachments: commentFiles };
      if (commentAssign) body.assigned_to = commentAssign;
      await api.post(`/tickets/${id}/comments`, body);
      setCommentBody("");
      setCommentFiles([]);
      setIsInternal(false);
      setCommentAssign("");
      refresh();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  useEffect(() => {
    if (ticket) { setRatingValue(ticket.rating || 0); setRatingComment(ticket.rating_comment || ""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.rating, ticket?.rating_comment]);

  const rateTicket = async () => {
    if (!ratingValue) return;
    try {
      const { data } = await api.post(`/tickets/${id}/rating`, { rating: ratingValue, comment: ratingComment });
      setTicket((t) => ({ ...t, ...data }));
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };

  const canAttach = ["super_admin", "project_manager", "team_member"].includes(user.role);

  const addWorklog = async (e) => {
    e.preventDefault();
    if (!worklog.description.trim() || !worklog.hours) return;
    try {
      await api.post(`/tickets/${id}/worklogs`, { ...worklog, hours: Number(worklog.hours) });
      setWorklog({ date: new Date().toISOString().slice(0, 10), hours: 1, description: "" });
      refresh();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  if (!ticket) {
    return <div className="text-sm text-slate-400" data-testid="ticket-loading">{error || "Loading…"}</div>;
  }

  return (
    <>
    <div className="space-y-5" data-testid="ticket-detail-page">
      <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5" data-testid="back-button">
        <ArrowLeft size={14} /> Back
      </button>

      {/* Header */}
      <div className="surface-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-mono text-blue-600 font-medium" data-testid="ticket-no">{ticket.ticket_no}</span>
              {fieldOn("request_type") && ticket.request_type && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="text-slate-500">
                    {ticket.request_type.replace(/_/g, " ")}
                    {subtypesText(ticket) ? ` · ${subtypesText(ticket)}` : ""}
                  </span>
                </>
              )}
              {!isClient && <ClientRepliedBadge at={ticket.client_replied_at} />}
              <span className="text-slate-300">·</span>
              <span className="text-slate-500">Created {timeAgo(ticket.created_at)}</span>
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1" data-testid="ticket-subject">{ticket.subject}</h1>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <StatusBadge status={ticket.status} />
              {fieldOn("priority") && <PriorityBadge priority={ticket.priority} />}
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">
                <FolderOpen size={12} />
                {ticket.project?.id ? (
                  <Link to={`/projects/${ticket.project.id}`} className="hover:text-blue-600 hover:underline">{ticket.project.name}</Link>
                ) : (ticket.project?.name || "—")}
              </span>
              {ticket.organization && (
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">
                  <Buildings size={12} />
                  {ticket.organization.id ? (
                    <Link to={`/organizations/${ticket.organization.id}`} className="hover:text-blue-600 hover:underline">{ticket.organization.name}</Link>
                  ) : ticket.organization.name}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="ticket-error">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-5">
          <div className="surface-card p-5">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-2">Description</div>
            {/<[a-z][\s\S]*>/i.test(ticket.description || "") ? (
              <div className="rich-content" data-testid="ticket-description"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ticket.description || "", { ADD_ATTR: ["target", "rel"] }) }} />
            ) : (
              <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="ticket-description">{ticket.description}</div>
            )}
            {fieldOn("reference_url") && ticket.reference_url && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-1">Reference URL</div>
                <a href={ticket.reference_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline break-all" data-testid="ticket-reference-url">
                  <LinkSimple size={14} /> {ticket.reference_url}
                </a>
              </div>
            )}
            {fieldOn("attachments") && ticket.attachments?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-2">Attachments ({ticket.attachments.length})</div>
                <AttachmentList attachments={ticket.attachments} />
              </div>
            )}
          </div>

          <Tabs defaultValue="conversation">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="conversation" data-testid="tab-conversation">Conversation</TabsTrigger>
              <TabsTrigger value="worklogs" data-testid="tab-worklogs">Work logs</TabsTrigger>
              <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
              {!isClient && <TabsTrigger value="journey" data-testid="tab-journey">Journey</TabsTrigger>}
            </TabsList>

            <TabsContent value="conversation" className="space-y-3 mt-3">
              <div className="space-y-3" data-testid="comments-list">
                {comments.length === 0 && (
                  <div className="text-xs text-slate-400 text-center py-8 border border-dashed border-slate-200 rounded-md">No comments yet — start the conversation.</div>
                )}
                {comments.map((c) => (
                  <div key={c.id} className={`border rounded-md p-3 ${c.kind === "status_change" ? "bg-blue-50/50 border-blue-200" : c.is_internal ? "bg-amber-50/50 border-amber-200" : "bg-white border-slate-200"}`} data-testid={`comment-${c.id}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[10px] font-semibold">
                        {initials(c.author?.name || "")}
                      </div>
                      <div className="text-sm font-medium text-slate-800">{c.author?.name || "Unknown"}</div>
                      <span className="text-[10px] uppercase tracking-wide text-slate-400">{ROLE_LABELS[c.author?.role] || ""}</span>
                      {c.kind === "status_change" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-blue-700 bg-blue-100 border border-blue-200 rounded-full px-1.5 py-0.5">
                          <ArrowRight size={10} /> {STATUS_LABELS[c.meta?.to] || c.meta?.to}
                        </span>
                      ) : c.is_internal && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-1.5 py-0.5">
                          <Lock size={10} /> Internal
                        </span>
                      )}
                      <span className="ml-auto text-xs text-slate-400">{timeAgo(c.created_at)}</span>
                    </div>
                    {c.body && <div className="text-sm text-slate-700 whitespace-pre-wrap pl-9">{c.body}</div>}
                    {c.attachments?.length > 0 && (
                      <div className="pl-9 mt-2"><AttachmentList attachments={c.attachments} /></div>
                    )}
                  </div>
                ))}
              </div>

              <form onSubmit={addComment} className="surface-card p-3" data-testid="comment-form">
                <Textarea
                  rows={3}
                  placeholder="Write a comment, ask a question, or share an update…"
                  value={commentBody}
                  onChange={(e) => setCommentBody(e.target.value)}
                  data-testid="comment-input"
                />
                <div className="mt-2">
                  <AttachmentUploader value={commentFiles} onChange={setCommentFiles} />
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap mt-2">
                  {canDev ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                        <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} data-testid="comment-internal-toggle" />
                        Mark as internal note (hidden from client)
                      </label>
                      <SearchableSelect
                        value={commentAssign || "none"}
                        onValueChange={(v) => setCommentAssign(v === "none" ? "" : v)}
                        options={[
                          { value: "none", label: "Assign to… (no change)" },
                          ...team.map((u) => ({ value: u.id, label: `${u.name} · ${ROLE_LABELS[u.role]}` })),
                        ]}
                        placeholder="Assign to…"
                        className="h-8 text-xs w-[190px]"
                        testId="comment-assign-select"
                      />
                    </div>
                  ) : <span />}
                  <Button type="submit" disabled={!commentBody.trim() && commentFiles.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="comment-submit-button">
                    <PaperPlaneTilt size={14} weight="regular" className="mr-1.5" /> Post comment
                  </Button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="worklogs" className="space-y-3 mt-3">
              <div className="flex items-center justify-between text-xs">
                <div className="text-slate-500">Total logged: <span className="font-mono text-slate-800 font-semibold">{totalLogged.toFixed(2)} h</span></div>
                <div className="text-slate-400">{worklogs.length} entries</div>
              </div>

              <div className="space-y-2" data-testid="worklogs-list">
                {worklogs.length === 0 && (
                  <div className="text-xs text-slate-400 text-center py-8 border border-dashed border-slate-200 rounded-md">No work logged yet.</div>
                )}
                {worklogs.map((w) => (
                  <div key={w.id} className="surface-card p-3 flex items-start gap-3" data-testid={`worklog-${w.id}`}>
                    <div className="w-8 h-8 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-mono font-semibold">{w.hours}h</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-800">{w.description}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{w.developer?.name || "—"} · {w.date}</div>
                    </div>
                  </div>
                ))}
              </div>

              {canDev && (
                <form onSubmit={addWorklog} className="surface-card p-3 space-y-3" data-testid="worklog-form">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Date</label>
                      <Input type="date" value={worklog.date} onChange={(e) => setWorklog({ ...worklog, date: e.target.value })} data-testid="worklog-date-input" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Hours</label>
                      <Input type="number" min="0.25" step="0.25" value={worklog.hours} onChange={(e) => setWorklog({ ...worklog, hours: e.target.value })} data-testid="worklog-hours-input" />
                    </div>
                    <div className="md:col-span-1 flex items-end">
                      <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="worklog-submit-button">Log work</Button>
                    </div>
                  </div>
                  <Textarea
                    rows={2}
                    placeholder="What did you work on?"
                    value={worklog.description}
                    onChange={(e) => setWorklog({ ...worklog, description: e.target.value })}
                    data-testid="worklog-description-input"
                  />
                </form>
              )}
            </TabsContent>

            <TabsContent value="activity" className="mt-3">
              <ol className="relative border-l border-slate-200 ml-3 space-y-4" data-testid="activity-list">
                {activities.length === 0 && (
                  <li className="text-xs text-slate-400 py-4">No activity yet.</li>
                )}
                {activities.map((a) => (
                  <li key={a.id} className="ml-4">
                    <div className="absolute w-2.5 h-2.5 bg-blue-500 rounded-full -left-[5px] mt-1.5" />
                    <div className="text-sm text-slate-700">
                      <span className="font-medium">{a.actor_name}</span> <span className="text-slate-500">{humanizeActivityAction(a.action)}</span>
                      {a.action === "reassigned" ? (
                        <span className="text-xs text-slate-500"> → {a.meta?.to ? (a.meta?.to_name || "Unknown") : "Unassigned"}</span>
                      ) : (
                        a.meta?.to && <span className="font-mono text-xs text-slate-500"> → {String(a.meta.to)}</span>
                      )}
                    </div>
                    {a.meta?.remark && (
                      <div className="text-xs text-slate-500 italic mt-0.5" data-testid={`activity-remark-${a.id}`}>“{a.meta.remark}”</div>
                    )}
                    <div className="text-xs text-slate-400 mt-0.5">{formatDate(a.created_at)}</div>
                  </li>
                ))}
              </ol>
            </TabsContent>

            {!isClient && (
              <TabsContent value="journey" className="mt-3" data-testid="journey-panel">
                {!journey ? (
                  <div className="text-xs text-slate-400 py-4">Loading journey…</div>
                ) : (
                  <>
                    <div className={`grid grid-cols-2 gap-2 mb-4 ${journey.summary.sla_breaches != null ? "md:grid-cols-6" : "md:grid-cols-5"}`}>
                      {[
                        ["Total open", fmtDur(journey.summary.total_open_seconds)],
                        ["To first assign", fmtDur(journey.summary.time_to_first_assignment_seconds)],
                        ["To resolution", fmtDur(journey.summary.time_to_resolution_seconds)],
                        ["Reassignments", journey.summary.reassignment_total],
                        ["Reopens", journey.summary.reopen_count],
                        ...(journey.summary.sla_breaches != null ? [["SLA breaches", journey.summary.sla_breaches]] : []),
                      ].map(([label, value]) => (
                        <div key={label} className="surface-card p-2.5" data-testid={`journey-stat-${label}`}>
                          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
                          <div className="text-sm font-mono text-slate-800 mt-0.5">{value}</div>
                        </div>
                      ))}
                    </div>

                    {journey.summary.clock && !journey.summary.clock.always_on && (
                      <div className="text-[11px] text-slate-400 -mt-2 mb-4" data-testid="journey-clock-note">
                        Durations measured in business hours — {formatWorkDays(journey.summary.clock.work_days)},{" "}
                        {journey.summary.clock.work_start}–{journey.summary.clock.work_end} ({journey.summary.clock.timezone})
                      </div>
                    )}

                    <ol className="relative border-l border-slate-200 ml-3 space-y-4" data-testid="journey-list">
                      {journey.segments.map((seg, i) => {
                        const total = journey.summary.total_open_seconds || 0;
                        const pct = total > 0 ? Math.round((100 * seg.duration_seconds) / total) : 0;
                        const isLongest = seg.status === journey.summary.longest_status;
                        return (
                          <li key={i} className="ml-4" data-testid={`journey-segment-${i}`}>
                            <div className={`absolute w-2.5 h-2.5 rounded-full -left-[5px] mt-1.5 ${seg.is_current ? "bg-emerald-500" : "bg-blue-500"}`} />
                            <div className="flex items-center gap-2 flex-wrap">
                              <StatusBadge status={seg.status} />
                              <span className="text-sm font-mono text-slate-700">{fmtDur(seg.duration_seconds)}{seg.is_current && " · now"}</span>
                              <span className="text-xs text-slate-400">({pct}%)</span>
                              {seg.sla_target_seconds != null && (
                                seg.duration_seconds > seg.sla_target_seconds ? (
                                  <span className="text-[10px] uppercase tracking-wide text-rose-700 bg-rose-100 border border-rose-200 rounded-full px-1.5 py-0.5" data-testid={`journey-sla-${i}`}>
                                    SLA {fmtDur(seg.sla_target_seconds)} · over by {fmtDur(seg.duration_seconds - seg.sla_target_seconds)}
                                  </span>
                                ) : (
                                  <span className="text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-1.5 py-0.5" data-testid={`journey-sla-${i}`}>
                                    SLA {fmtDur(seg.sla_target_seconds)} · within
                                  </span>
                                )
                              )}
                              {isLongest && (
                                <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-1.5 py-0.5">longest</span>
                              )}
                            </div>
                            <div className="mt-1 h-1.5 w-full max-w-[240px] bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-400" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {seg.entered_by ? <>moved by <span className="font-medium">{seg.entered_by.name}</span></> : "created"}
                              {seg.remark && <span className="italic"> · "{seg.remark}"</span>}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5" data-testid={`journey-assignees-${i}`}>
                              {seg.assignees.map((it, j) => (
                                <span key={j}>
                                  {j > 0 && <span className="text-slate-400"> → reassigned to </span>}
                                  <span className="font-medium">{it.assignee ? it.assignee.name : "Unassigned"}</span>
                                </span>
                              ))}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </>
                )}
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* Sidebar */}
        <aside className="space-y-3 lg:sticky lg:top-20 self-start" data-testid="ticket-sidebar">
          <div className="surface-card p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Status</div>
            {statusOptions.length === 0 ? (
              <StatusBadge status={ticket.status} />
            ) : (
              <SearchableSelect
                value={ticket.status}
                onValueChange={openStatusModal}
                options={statusOptions.map((s) => ({ value: s.key, label: s.label }))}
                testId="sidebar-status-select"
              />
            )}
          </div>

          {fieldOn("priority") && (
          <div className="surface-card p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Priority</div>
            {canEditPriority ? (
              <SearchableSelect
                value={ticket.priority}
                onValueChange={onPriorityChange}
                options={(priorityOpts.includes(ticket.priority)
                  ? priorityOpts
                  : [ticket.priority, ...priorityOpts].filter(Boolean)
                ).map((p) => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) }))}
                testId="sidebar-priority-select"
              />
            ) : <PriorityBadge priority={ticket.priority} />}
          </div>
          )}

          {canDev && (
            <div className="surface-card p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Assignee</div>
                {ticket.assigned_to !== user.id && (
                  <button type="button" onClick={() => onAssign(user.id)} className="text-[11px] font-medium text-blue-600 hover:text-blue-700" data-testid="assign-to-me">
                    Assign to me
                  </button>
                )}
              </div>
              <SearchableSelect
                value={ticket.assigned_to || "unassigned"}
                onValueChange={(v) => onAssign(v === "unassigned" ? null : v)}
                options={[
                  { value: "unassigned", label: "Unassigned" },
                  ...team.map((u) => ({ value: u.id, label: `${u.name} · ${ROLE_LABELS[u.role]}` })),
                ]}
                placeholder="Unassigned"
                testId="sidebar-assignee-select"
              />
            </div>
          )}

          {canDev && ticket.sla_due_at && (() => {
            const sla = slaState(ticket.sla_due_at, ticket.sla_started_at);
            if (!sla) return null;
            const cls = sla.tone === "rose" ? "text-rose-600" : sla.tone === "amber" ? "text-amber-600" : "text-slate-500";
            return (
              <div className="surface-card p-4 space-y-2" data-testid="sla-card">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">SLA</div>
                <div className="text-xs text-slate-500">Due {formatDate(ticket.sla_due_at)}</div>
                <div className={`text-sm font-medium ${cls}`}>{sla.label}</div>
              </div>
            );
          })()}

          {(ticket.resolved_at || ticket.closed_at) && (
            <div className="surface-card p-4 space-y-3" data-testid="satisfaction-card">
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Satisfaction</div>
              {isClient ? (
                <>
                  <StarRating value={ratingValue} onChange={setRatingValue} />
                  <Textarea rows={2} placeholder="Optional feedback on the resolution…" value={ratingComment} onChange={(e) => setRatingComment(e.target.value)} data-testid="rating-comment" />
                  <Button type="button" size="sm" disabled={!ratingValue} onClick={rateTicket} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="rating-submit">
                    {ticket.rating ? "Update rating" : "Submit rating"}
                  </Button>
                </>
              ) : (
                ticket.rating ? (
                  <div className="space-y-1">
                    <StarRating value={ticket.rating} />
                    {ticket.rating_comment && <div className="text-xs text-slate-600 italic">"{ticket.rating_comment}"</div>}
                  </div>
                ) : <div className="text-xs text-slate-400">Not rated yet.</div>
              )}
            </div>
          )}

          <div className="surface-card p-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-2">Details</div>
            <dl className="text-xs space-y-2.5">
              {fieldOn("category") && <div className="flex justify-between gap-2"><dt className="text-slate-500">Category</dt><dd className="text-slate-800 font-medium">{ticket.category || "—"}</dd></div>}
              {fieldOn("impact") && <div className="flex justify-between gap-2"><dt className="text-slate-500">Impact</dt><dd className="text-slate-800 font-medium capitalize">{(ticket.impact || "—").replace(/_/g, " ")}</dd></div>}
              {fieldOn("request_type") && <div className="flex justify-between gap-2"><dt className="text-slate-500">Type</dt><dd className="text-slate-800 font-medium">{ticket.request_type?.replace(/_/g, " ") || "—"}</dd></div>}
              {fieldOn("request_type") && subtypesText(ticket) && <div className="flex justify-between gap-2"><dt className="text-slate-500">Sub-type{(ticket.request_subtypes?.length || 0) > 1 ? "s" : ""}</dt><dd className="text-slate-800 font-medium text-right">{subtypesText(ticket)}</dd></div>}
              <div className="flex justify-between gap-2"><dt className="text-slate-500">Project</dt><dd className="text-slate-800 font-medium">{ticket.project?.id ? (<Link to={`/projects/${ticket.project.id}`} className="hover:text-blue-600 hover:underline">{ticket.project.name}</Link>) : (ticket.project?.name || "—")}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">Created</dt><dd className="text-slate-800 font-medium">{formatDate(ticket.created_at)}</dd></div>
              <div className="flex justify-between gap-2"><dt className="text-slate-500">Updated</dt><dd className="text-slate-800 font-medium">{formatDate(ticket.updated_at)}</dd></div>
            </dl>
          </div>

          {customFieldDefs.length > 0 && (
            <div className="surface-card p-4">
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-2">Additional details</div>
              <dl className="text-xs space-y-2.5">
                {customFieldDefs.map((f) => {
                  const v = ticket.custom_fields?.[f.key];
                  const has = v !== undefined && v !== null && v !== "";
                  return (
                    <div key={f.id} className="flex justify-between gap-2">
                      <dt className="text-slate-500">{f.label}</dt>
                      <dd className="text-slate-800 font-medium text-right">{has ? String(v) : "—"}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          )}

          <div className="surface-card p-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mb-2">People</div>
            <div className="space-y-2">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-semibold">{initials(ticket.creator?.name || "")}</div>
                <div>
                  <div className="text-sm font-medium text-slate-800">
                    {ticket.creator?.id ? (
                      <Link to={`/users/${ticket.creator.id}`} className="hover:text-blue-600 hover:underline">{ticket.creator.name}</Link>
                    ) : (ticket.creator?.name || "—")}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Reporter</div>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[11px] font-semibold">{ticket.assignee ? initials(ticket.assignee.name) : "—"}</div>
                <div>
                  <div className="text-sm font-medium text-slate-800">
                    {ticket.assignee?.id ? (
                      <Link to={`/users/${ticket.assignee.id}`} className="hover:text-blue-600 hover:underline">{ticket.assignee.name}</Link>
                    ) : (ticket.assignee?.name || "Unassigned")}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Assignee</div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>

    {/* Remark modal — rendered outside main div via fragment */}
    {/* Remark modal */}
    {pendingStatus && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        data-testid="remark-modal-overlay"
        onClick={(e) => { if (e.target === e.currentTarget) cancelStatusModal(); }}
      >
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4" data-testid="remark-modal">
          <div className="text-base font-semibold text-slate-900">Change status</div>
          <p className="text-sm text-slate-600">
            Moving to <span className="font-medium text-slate-800">{statusOptions.find((s) => s.key === pendingStatus)?.label || pendingStatus}</span>.
            Please provide a remark explaining this change.
          </p>
          <div className="space-y-1">
            <textarea
              autoFocus
              rows={4}
              placeholder="Describe the reason for this status change…"
              value={remark}
              onChange={(e) => { setRemark(e.target.value); if (remarkErr) setRemarkErr(""); }}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              data-testid="remark-textarea"
            />
            {remarkErr && <p className="text-xs text-rose-600" data-testid="remark-error">{remarkErr}</p>}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={cancelStatusModal} disabled={remarkSubmitting} data-testid="remark-cancel">Cancel</Button>
            <Button onClick={confirmStatusChange} disabled={remarkSubmitting} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="remark-confirm">
              {remarkSubmitting ? "Saving…" : "Confirm"}
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
